import { useQuery } from 'react-query';
import { usePublicApiClient } from '../usePublicApiClient';
import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi';
import { ContentfulCurrentActiveEntries } from '@jbhi-fi/apis/src/contentful/types';
import { ContentfulClient } from '@jbhi-fi/apis/src/contentful/ContentfulClient';

export type UseContentfulCurrentWebsiteElementsProps = {
  contentfulThemeApiClient?: ContentfulThemeApiClient;
  contentfulclient?: ContentfulClient;
};

type ContentfulCurrentActiveEntriesResult = Omit<ContentfulCurrentActiveEntries['fields'], 'name' | 'snackbar'>;

export const useContentfulCurrentWebsiteElements = (props: UseContentfulCurrentWebsiteElementsProps = {}) => {
  const { contentfulThemeApiClient: contentfulThemeApiClientContext, contentfulClient: contentfulClientContext } =
    usePublicApiClient();
  const contentfulThemeApiClient = props.contentfulThemeApiClient || contentfulThemeApiClientContext;
  const contentfulClient = props.contentfulclient || contentfulClientContext;
  if (!contentfulThemeApiClient) {
    throw new Error(
      'useContentfulCurrentWebsiteElements must be provided with a contentfulThemeApiClient via PublicApiClientProvider or props'
    );
  }
  const isPreview = window.Jbhifi?.isContentPreview || false;

  const { isLoading, data: activeEntryResponse } = useQuery({
    enabled: !!contentfulThemeApiClient && !!contentfulClient,
    queryKey: ['useContentfulCurrentWebsiteElements', 'currentActiveEntry'],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const baseQueryOptions = { content_type: 'currentActiveHomepage', include: '0' };
      const shouldUseThemeApi = isPreview || !contentfulClient;
      const queryOptions = shouldUseThemeApi ? { ...baseQueryOptions, cache: 'medium' } : baseQueryOptions;

      return shouldUseThemeApi
        ? contentfulThemeApiClient!.getEntries(queryOptions)
        : contentfulClient!.getEntries(queryOptions);
    },
  });
  const currentActiveEntry = activeEntryResponse?.data?.items?.[0] as ContentfulCurrentActiveEntries;
  const currentActiveEntryFields = currentActiveEntry?.fields as ContentfulCurrentActiveEntriesResult;
  const currentWebsiteEntryIds = currentActiveEntryFields
    ? (Object.fromEntries(
        Object.entries(currentActiveEntryFields)
          .filter(([, value]) => value.sys?.id)
          .map(([key, value]) => [key, value.sys.id] as const)
      ) as Partial<Record<keyof ContentfulCurrentActiveEntriesResult, string>>)
    : undefined;
  return { isLoading, currentWebsiteEntryIds };
};
